import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, Lock, Linkedin } from "lucide-react";
import { api } from "@/lib/api";

const ADVISOR_SUBJECT = "Advisory Board Interest";

/**
 * Inline form for senior operators expressing interest in joining the
 * Toulios Advisory Board. Submits to the existing /api/contact endpoint
 * with subject = "Advisory Board Interest" so it lands in the contact_messages
 * collection alongside general enquiries.
 */
export default function AdvisorInterestForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    linkedin: "",
    role: "",
    pitch: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const change = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;

    if (!form.name || !form.email || !form.pitch) {
      toast.error("Please share at least your name, email and a few lines on what you'd bring.");
      return;
    }

    setSubmitting(true);
    try {
      const message =
        `Current role: ${form.role || "—"}\n` +
        `LinkedIn: ${form.linkedin || "—"}\n\n` +
        `What I'd bring to the board:\n${form.pitch}`;

      await api.post("/contact", {
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        subject: ADVISOR_SUBJECT,
        message,
      });
      setDone(true);
      toast.success("Received. We'll be in touch within 1 to 2 business days.");
    } catch (err) {
      toast.error("Could not submit. Please email hello@touliosadvisory.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="border border-gold/30 bg-[#0a0a0a] p-8 md:p-10 text-center"
        data-testid="advisor-form-success"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 border border-gold/40 bg-[#070707]">
          <Check className="w-5 h-5 text-gold" />
        </div>
        <h3 className="mt-6 font-display text-2xl tracking-tight">Thank you.</h3>
        <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-md mx-auto">
          Your interest in the Toulios Advisory Board has been received. A
          senior consultant will review your background and respond within
          1 to 2 business days.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="advisor-interest-form"
      className="space-y-5"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={change("name")}
            required
            className="mt-2 w-full bg-transparent border border-white/[0.08] focus:border-gold/60 px-3 py-3 text-sm text-white outline-none transition-colors"
            data-testid="advisor-name"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Email *</label>
          <input
            type="email"
            value={form.email}
            onChange={change("email")}
            required
            className="mt-2 w-full bg-transparent border border-white/[0.08] focus:border-gold/60 px-3 py-3 text-sm text-white outline-none transition-colors"
            data-testid="advisor-email"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Current firm</label>
          <input
            type="text"
            value={form.company}
            onChange={change("company")}
            className="mt-2 w-full bg-transparent border border-white/[0.08] focus:border-gold/60 px-3 py-3 text-sm text-white outline-none transition-colors"
            data-testid="advisor-firm"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Current role / title</label>
          <input
            type="text"
            value={form.role}
            onChange={change("role")}
            className="mt-2 w-full bg-transparent border border-white/[0.08] focus:border-gold/60 px-3 py-3 text-sm text-white outline-none transition-colors"
            data-testid="advisor-role"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 flex items-center gap-2">
          <Linkedin className="w-3 h-3" /> LinkedIn URL
        </label>
        <input
          type="url"
          value={form.linkedin}
          onChange={change("linkedin")}
          placeholder="https://www.linkedin.com/in/..."
          className="mt-2 w-full bg-transparent border border-white/[0.08] focus:border-gold/60 px-3 py-3 text-sm text-white outline-none transition-colors"
          data-testid="advisor-linkedin"
        />
      </div>

      <div>
        <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">What you&rsquo;d bring to the board *</label>
        <textarea
          value={form.pitch}
          onChange={change("pitch")}
          required
          rows={5}
          placeholder="Briefly: your background, the specific lens you'd bring to client engagements, and what you'd want to get from the role in return."
          className="mt-2 w-full bg-transparent border border-white/[0.08] focus:border-gold/60 px-3 py-3 text-sm text-white outline-none transition-colors resize-none"
          data-testid="advisor-pitch"
        />
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
        <p className="text-xs text-zinc-500 max-w-md leading-relaxed flex items-start gap-2">
          <Lock className="w-3 h-3 mt-0.5 text-gold flex-shrink-0" />
          <span>
            Submissions are confidential. Handled per our{" "}
            <Link to="/trust" className="text-zinc-300 hover:text-gold underline-offset-2 hover:underline">
              Trust & Privacy
            </Link>{" "}
            policy.
          </span>
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed min-w-[200px]"
          data-testid="advisor-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Submitting
            </>
          ) : (
            <>Submit interest</>
          )}
        </button>
      </div>
    </form>
  );
}
